import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

/* ---------- helpers ---------- */
const num = (v) => (isNaN(Number(v)) ? 0 : Number(v));
const Star = () => <span className="text-red-600 font-bold ml-0.5">*</span>;
const euro = (v) =>
  (Number(v) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " €";

export default function QuoteServices({
  prestations,
  setPrestations,
  serviceLabel,
  setServiceLabel,
  servicePrice,
  setServicePrice,
  distanceKm,
  setDistanceKm,
  wearCoef,
  setWearCoef,
  fuelCost,
  setFuelCost,
  salaries,
  setSalaries,
  otherCharges,
  setOtherCharges,
  commercialMargin,
  setCommercialMargin,
  serviceType, // ← Éco / Standard / Luxe (depuis le formulaire)
  providerType = "internal",
  setProviderType,
  subcontractorId,
  setSubcontractorId,
  subcontractAmount = "",
  setSubcontractAmount,
  subcontractors = [],
  discountPercent = "",
  setDiscountPercent,
}) {
  const { t } = useTranslation();

  /* ---------- libellé auto pour "Service déménagement" ---------- */
  const defaultServiceLabel = useMemo(() => {
    const base = t("quote.movingService", "Service déménagement");
    return `${base}${serviceType ? ` ${serviceType}` : ""}`.trim();
  }, [t, serviceType]);

  const lastAutoLabelRef = useRef(defaultServiceLabel);

  // Si serviceType (ou la langue) change ET que l'utilisateur n'a pas modifié le champ
  // (c-à-d le champ == ancien libellé auto), alors on remplace par le nouveau auto
  useEffect(() => {
    const prevAuto = lastAutoLabelRef.current;
    if ((serviceLabel || "").trim() === (prevAuto || "").trim()) {
      setServiceLabel(defaultServiceLabel);
    }
    lastAutoLabelRef.current = defaultServiceLabel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultServiceLabel]);

  /* ---------- Calculs ---------- */
  const salariesTotal = useMemo(
    () => (salaries || []).reduce((s, x) => s + num(x.salary), 0),
    [salaries]
  );

  const othersTotal = useMemo(
    () => (otherCharges || []).reduce((s, x) => s + num(x.amount), 0),
    [otherCharges]
  );

  const wearCostLive = useMemo(
    () => num(distanceKm) * num(wearCoef),
    [distanceKm, wearCoef]
  );

  // Lignes standard
  const lineService = useMemo(
    () => ({
      description: (serviceLabel || defaultServiceLabel).trim(),
      prix: num(servicePrice),
      quantite: 1,
      total: num(servicePrice),
      __fixed: "SERVICE",
      // Identifie qui réalise cette première prestation : équipe interne ou sous-traitant.
      provider_type: providerType === "subcontractor" ? "subcontractor" : "internal",
      subcontractor_id: providerType === "subcontractor" ? (subcontractorId || null) : null,
      // Montant HT confié au sous-traitant : base du calcul de commission sur la marge.
      subcontract_amount: providerType === "subcontractor" ? num(subcontractAmount) : null,
    }),
    [serviceLabel, servicePrice, defaultServiceLabel, providerType, subcontractorId, subcontractAmount]
  );

  const lineLabor = useMemo(
    () => ({
      description: t("quote.labor", "Main d'œuvre"),
      prix: salariesTotal,
      quantite: 1,
      total: salariesTotal,
      __fixed: "LABOR",
    }),
    [salariesTotal, t]
  );

  // ⚠️ sans les salaires (déjà dans "Main d'œuvre")
  const mgmtAndOps = useMemo(
    () => wearCostLive + num(fuelCost) + othersTotal + num(commercialMargin),
    [wearCostLive, fuelCost, othersTotal, commercialMargin]
  );

  const lineMgmt = useMemo(
    () => ({
      description: t("quote.managementFees", "Frais de gestion & exploitation"),
      prix: mgmtAndOps,
      quantite: 1,
      total: mgmtAndOps,
      __fixed: "MGMT",
    }),
    [mgmtAndOps, t]
  );

  const extrasHT = useMemo(
    () =>
      (prestations || [])
        .filter((p) => !p.__fixed)
        .reduce((sum, p) => {
          const prix = num(p.prix);
          const qte =
            p.quantite == null || p.quantite === "" ? 1 : num(p.quantite); // 0 reste 0
          return sum + prix * qte;
        }, 0),
    [prestations]
  );

  const totalHT = useMemo(
    () => num(lineService.total) + num(lineLabor.total) + num(lineMgmt.total) + num(extrasHT),
    [lineService, lineLabor, lineMgmt, extrasHT]
  );
  const totalTVA = useMemo(() => totalHT * 0.2, [totalHT]);
  const totalTTC = useMemo(() => totalHT + totalTVA, [totalHT, totalTVA]);

  // Alerte anti double-comptage (non bloquante)
  const hasRiskOfDoubleCount = useMemo(() => {
    const sp = num(servicePrice);
    return (
      sp > 0 &&
      (num(salariesTotal) > 0 ||
        num(wearCostLive) > 0 ||
        num(fuelCost) > 0 ||
        num(othersTotal) > 0 ||
        num(commercialMargin) > 0)
    );
  }, [servicePrice, salariesTotal, wearCostLive, fuelCost, othersTotal, commercialMargin]);

  /* ---------- Synchronisation vers le parent ---------- */
  useEffect(() => {
    const extras = (prestations || []).filter((p) => !p.__fixed);
    const finalPrestas = [lineService, lineLabor, lineMgmt, ...extras];

    const same =
      prestations &&
      prestations.length === finalPrestas.length &&
      prestations.every((p, i) => shallowEqualPresta(p, finalPrestas[i]));

    if (!same) setPrestations(finalPrestas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineService, lineLabor, lineMgmt, prestations, setPrestations]);

  return (
    <div className="space-y-6">
      {/* Lignes standard envoyées */}
      <div className="bg-base-100 border border-base-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-blue-800">
            📦 {t("quote.standardLines", "Lignes standard envoyées")} <Star />
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {/* Service déménagement (libre) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-8">
              <label className="text-sm text-base-content/70">
                {t("quote.movingService", "Service déménagement (libre, hors salaires & frais)")}
              </label>
              <input
                type="text"
                value={serviceLabel}
                onChange={(e) => setServiceLabel(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                placeholder={defaultServiceLabel}
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-sm text-base-content/70">
                {t("quote.servicePrice", "Prix du service (forfait)")}
              </label>
              <input
                type="number"
                step="0.01"
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-right focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
              {hasRiskOfDoubleCount && (
                <div className="mt-1 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1">
                  {t(
                    "quote.doubleCountWarn",
                    "Attention : ce prix s’ajoute à la Main d’œuvre et aux Frais de gestion. Ne mettez pas ici un montant 'tout compris'."
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Réalisé par : équipe interne ou sous-traitant */}
          <div className="rounded-xl border border-base-300 bg-base-200 p-3">
            <label className="text-xs font-semibold text-base-content/60 uppercase tracking-wide">
              {t("quote.providerLabel", "Réalisé par")}
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <div className="flex rounded-lg overflow-hidden border border-base-300 text-sm font-medium">
                <button
                  type="button"
                  onClick={() => setProviderType?.("internal")}
                  className={`px-3 py-1.5 transition-colors ${
                    providerType === "internal" ? "bg-blue-600 text-white" : "bg-base-100 text-base-content/60 hover:bg-base-200"
                  }`}
                >
                  {t("quote.providerInternal", "Équipe interne")}
                </button>
                <button
                  type="button"
                  onClick={() => setProviderType?.("subcontractor")}
                  className={`px-3 py-1.5 transition-colors border-l border-base-300 ${
                    providerType === "subcontractor" ? "bg-orange-500 text-white" : "bg-base-100 text-base-content/60 hover:bg-base-200"
                  }`}
                >
                  {t("quote.providerSubcontractor", "Sous-traité")}
                </button>
              </div>
              {providerType === "subcontractor" && (
                <select
                  value={subcontractorId || ""}
                  onChange={(e) => setSubcontractorId?.(e.target.value ? Number(e.target.value) : null)}
                  className="px-3 py-1.5 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:ring-2 focus:ring-orange-300 outline-none min-w-[200px]"
                >
                  <option value="">{t("quote.selectSubcontractor", "Choisir un sous-traitant…")}</option>
                  {subcontractors.map((sc) => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                </select>
              )}
            </div>

            {providerType === "subcontractor" && (
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-base-content/60">
                    {t("quote.subcontractAmount", "Montant confié au sous-traitant (HT)")}
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={subcontractAmount}
                      onChange={(e) => setSubcontractAmount?.(e.target.value)}
                      placeholder="0.00"
                      className="w-40 px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-right focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    />
                    <span className="text-sm text-base-content/50">€</span>
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 pb-1">
                  <span className="text-[11px] uppercase tracking-wide text-base-content/50 font-semibold">
                    {t("quote.commissionBase", "Marge HT (base commission)")}
                  </span>
                  <span className={`text-lg font-extrabold ${(totalHT - num(subcontractAmount)) < 0 ? "text-rose-500" : "text-emerald-600"}`}>
                    {euro(Math.max(0, totalHT - num(subcontractAmount)))}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Main d'œuvre */}
          <ReadOnlyLine
            label={t("quote.labor", "Main d'œuvre")}
            value={euro(salariesTotal)}
            hint={t("quote.autoFromSalaries", "Calculé automatiquement depuis les salaires")}
          />

          {/* Frais de gestion & exploitation */}
          <ReadOnlyLine
            label={t("quote.managementFees", "Frais de gestion & exploitation")}
            value={euro(mgmtAndOps)}
            hint={
              `${t(
                "quote.autoFromParams",
                "Usure + Carburant + Autres charges + Marge commerciale"
              )} · ` +
              `${t("quote.breakdown", "Détail")} : ` +
              `${t("quote.wear", "Usure")}=${euro(wearCostLive)} · ` +
              `${t("quote.fuel", "Carburant")}=${euro(num(fuelCost))} · ` +
              `${t("quote.otherCharges", "Autres charges")}=${euro(othersTotal)} · ` +
              `${t("quote.commercialMarginShort", "Marge")}=${euro(num(commercialMargin))}`
            }
          />
        </div>
      </div>

      {/* Paramètres & coûts */}
      <div className="bg-base-100 border border-base-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-blue-800 mb-3">
          ⚙️ {t("quote.infoAndCalc", "Paramètres & coûts")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FieldNumber
            label={t("quote.distance", "Distance à parcourir")}
            value={distanceKm}
            onChange={(v) => setDistanceKm(num(v))}
            suffix="km"
          />
          <FieldNumber
            label={t("quote.wearCoef", "Coefficient d’usure véhicule")}
            value={wearCoef}
            onChange={(v) => setWearCoef(num(v))}
          />
          <FieldNumber
            label={t("quote.fuel", "Coût carburant")}
            value={fuelCost}
            onChange={(v) => setFuelCost(num(v))}
            suffix="€"
          />
          <FieldNumber
            label={t("quote.commercialMargin", "Marge commerciale")}
            value={commercialMargin}
            onChange={(v) => setCommercialMargin(num(v))}
            suffix="€"
          />
        </div>
      </div>

      {/* Salariés */}
      <div className="bg-base-100 border border-base-200 rounded-2xl p-6 shadow-sm">
        <SalariesSection salaries={salaries} setSalaries={setSalaries} t={t} />
      </div>

      {/* Autres charges */}
      <div className="bg-base-100 border border-base-200 rounded-2xl p-6 shadow-sm">
        <OtherChargesSection
          otherCharges={otherCharges}
          setOtherCharges={setOtherCharges}
          t={t}
        />
      </div>

      {/* Lignes supplémentaires */}
      <ExtrasTable
        extras={(prestations || []).filter((p) => !p.__fixed)}
        setExtras={(next) => {
          setPrestations([
            lineService,
            lineLabor,
            lineMgmt,
            ...next.map(stripFixedFlag),
          ]);
        }}
        t={t}
      />

      {/* Résumé */}
      <div className="bg-base-200 border border-base-200 rounded-2xl p-5 shadow-sm">
        <h3 className="text-lg font-bold text-base-content mb-3">
          🧮 {t("quote.estimate", "Estimation")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <SummaryItem label={t("quote.labor", "Main d'œuvre")} value={euro(lineLabor.total)} />
          <SummaryItem label={t("quote.movingService", "Service déménagement")} value={euro(lineService.total)} />
          <SummaryItem label={t("quote.managementFees", "Frais de gestion & exploitation")} value={euro(lineMgmt.total)} />
          <SummaryItem label={t("quote.totalHT", "Total HT")} value={euro(totalHT)} />
          <SummaryItem label={`${t("quote.tva", "TVA")} (20%)`} value={euro(totalTVA)} />
          <SummaryItem label={t("quote.totalTTC", "Total TTC")} value={euro(totalTTC)} strong />
        </div>
      </div>

      {/* Remise commerciale */}
      <div className="bg-base-200 border border-base-200 rounded-2xl p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content flex items-center gap-2">
              🏷️ {t("quote.discount", "Remise commerciale")}
            </label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={discountPercent}
                onChange={(e) => setDiscountPercent?.(e.target.value)}
                placeholder="0"
                className="w-24 px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-right focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
              <span className="text-sm text-base-content/60">%</span>
            </div>
          </div>

          {num(discountPercent) > 0 && (
            <div className="flex flex-wrap items-end gap-5">
              <MiniStat
                label={`${t("quote.discountAmount", "Remise")} (${num(discountPercent)}%)`}
                value={`− ${euro(totalTTC * num(discountPercent) / 100)} TTC`}
                tone="text-emerald-600"
              />
              <MiniStat
                label={t("quote.finalAmountHT", "Net HT")}
                value={euro(totalHT * (1 - num(discountPercent) / 100))}
                tone="text-base-content"
              />
              <MiniStat
                label={t("quote.finalAmount", "Net à payer TTC")}
                value={euro(totalTTC * (1 - num(discountPercent) / 100))}
                tone="text-primary"
                strong
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "text-base-content", strong = false }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-base-content/50 font-semibold">{label}</span>
      <span className={`${strong ? "text-xl font-extrabold" : "text-base font-bold"} ${tone}`}>{value}</span>
    </div>
  );
}

/* ----------------- Sous-composants ----------------- */

function FieldNumber({ label, value, onChange, suffix }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm font-semibold text-base-content">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          step="0.01"
          value={value ?? 0}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
        />
        {suffix && <span className="text-sm text-base-content/60">{suffix}</span>}
      </div>
    </div>
  );
}

function SummaryItem({ label, value, strong = false }) {
  return (
    <div className="bg-base-100/70 backdrop-blur-sm rounded-xl border border-base-200 p-3 flex flex-col">
      <div className="text-[11px] uppercase tracking-wide text-base-content/60 font-semibold truncate">
        {label}
      </div>
      <div className={`mt-1 ${strong ? "text-xl font-extrabold text-emerald-700" : "text-lg font-bold text-base-content"}`}>
        {value}
      </div>
    </div>
  );
}

function ReadOnlyLine({ label, value, hint }) {
  return (
    <div className="bg-primary/5 border rounded-xl p-4 flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-base-content">{label}</div>
        {hint && <div className="text-xs text-blue-700/80">{hint}</div>}
      </div>
      <div className="text-lg font-bold text-base-content">{value}</div>
    </div>
  );
}

function SalariesSection({ salaries, setSalaries, t }) {
  const updateSalary = (i, field, value) => {
    const next = [...(salaries || [])];
    next[i] = { ...next[i], [field]: field === "salary" ? num(value) : value };
    setSalaries(next);
  };
  const addSalary = () =>
    setSalaries([...(salaries || []), { name: "", salary: 0 }]);
  const removeSalary = (i) =>
    setSalaries((salaries || []).filter((_, idx) => idx !== i));

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-blue-800">👷 {t("quote.employees")}</h3>
        <button
          type="button"
          onClick={addSalary}
          className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-content text-sm shadow"
        >
          + {t("quote.addEmployee")}
        </button>
      </div>
      <div className="space-y-3">
        {(salaries || []).map((s, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4">
              <label className="text-sm text-base-content/70">{t("quote.employeeName")}</label>
              <input
                type="text"
                value={s.name}
                onChange={(e) => updateSalary(i, "name", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                placeholder={t("quote.employeeName")}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-base-content/70">{t("quote.salary")}</label>
              <input
                type="number"
                step="0.01"
                value={s.salary}
                onChange={(e) => updateSalary(i, "salary", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
            </div>
            <div className="md:col-span-6 flex justify-end">
              <button
                type="button"
                onClick={() => removeSalary(i)}
                className="px-3 py-1.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 text-sm"
              >
                {t("buttons.remove", "Remove")}
              </button>
            </div>
          </div>
        ))}
        {(salaries || []).length === 0 && (
          <p className="text-sm text-base-content/50">{t("quote.noResults", "No results")}</p>
        )}
      </div>
    </>
  );
}

function OtherChargesSection({ otherCharges, setOtherCharges, t }) {
  const updateOther = (i, field, value) => {
    const next = [...(otherCharges || [])];
    next[i] = { ...next[i], [field]: field === "amount" ? num(value) : value };
    setOtherCharges(next);
  };
  const addOther = () =>
    setOtherCharges([...(otherCharges || []), { label: "", amount: 0 }]);
  const removeOther = (i) =>
    setOtherCharges((otherCharges || []).filter((_, idx) => idx !== i));

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-blue-800">🧮 {t("quote.otherCharges")}</h3>
      </div>
      <div className="space-y-3">
        {(otherCharges || []).map((c, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4">
              <label className="text-sm text-base-content/70">{t("quote.chargeLabel")}</label>
              <input
                type="text"
                value={c.label}
                onChange={(e) => updateOther(i, "label", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                placeholder={t("quote.chargeLabel")}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-base-content/70">{t("quote.chargeAmount")}</label>
              <input
                type="number"
                step="0.01"
                value={c.amount}
                onChange={(e) => updateOther(i, "amount", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
            </div>
            <div className="md:col-span-6 flex justify-between">
              <button
                type="button"
                onClick={addOther}
                className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-content text-sm shadow"
              >
                + {t("quote.addCharge")}
              </button>
              <button
                type="button"
                onClick={() => removeOther(i)}
                className="px-3 py-1.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 text-sm"
              >
                {t("buttons.remove", "Remove")}
              </button>
            </div>
          </div>
        ))}
        {(otherCharges || []).length === 0 && (
          <div className="flex justify-start">
            <button
              type="button"
              onClick={addOther}
              className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-content text-sm shadow"
            >
              + {t("quote.addCharge")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function ExtrasTable({ extras, setExtras, t }) {
  const updateExtra = (i, field, value) => {
    const next = [...(extras || [])];
    next[i] = {
      ...next[i],
      [field]: field === "description" ? value : num(value),
    };
    if (field === "prix" || field === "quantite") {
      const prix = num(next[i].prix);
      const qte =
        next[i].quantite == null || next[i].quantite === ""
          ? 1
          : num(next[i].quantite); // 0 reste 0
      next[i].total = prix * qte;
    }
    setExtras(next);
  };

  const addExtra = () =>
    setExtras([...(extras || []), { description: "", prix: 0, quantite: 1, total: 0 }]);

  const removeExtra = (i) =>
    setExtras((extras || []).filter((_, idx) => idx !== i));

  return (
    <div className="bg-base-100 border border-base-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-blue-800">
          🧾 {t("quote.additionalLines", "Autres lignes (facultatif)")}
        </h3>
        <button
          type="button"
          onClick={addExtra}
          className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-content text-sm shadow"
        >
          + {t("buttons.add", "Ajouter")}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-base-100 rounded-xl border">
          <thead>
            <tr className="bg-base-200 text-base-content">
              <th className="px-3 py-2 text-left">{t("quote.description")}</th>
              <th className="px-3 py-2 text-right">{t("quote.price")}</th>
              <th className="px-3 py-2 text-right">{t("quote.quantity")}</th>
              <th className="px-3 py-2 text-right">{t("quote.totalHT")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(extras || []).map((p, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={p.description}
                    onChange={(e) => updateExtra(i, "description", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                    placeholder={t("quote.description")}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    step="0.01"
                    value={p.prix}
                    onChange={(e) => updateExtra(i, "prix", e.target.value)}
                    className="w-28 px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-right focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    step="1"
                    value={p.quantite}
                    onChange={(e) => updateExtra(i, "quantite", e.target.value)}
                    className="w-20 px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-right focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                  />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {(num(p.total) || 0).toFixed(2)} €
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeExtra(i)}
                    className="px-2 py-1.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-600"
                    aria-label="remove row"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
            {(!extras || extras.length === 0) && (
              <tr>
                <td colSpan={5} className="text-center text-base-content/50 py-4">
                  {t("quote.noResults", "No results")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- utils ---------- */
function shallowEqualPresta(a, b) {
  // provider_type / subcontractor_id inclus : sans eux, changer le prestataire
  // (sans toucher au reste) ne resynchronise pas les prestations → non persisté.
  const keys = ["description", "prix", "quantite", "total", "__fixed", "provider_type", "subcontractor_id", "subcontract_amount"];
  return keys.every((k) => (a?.[k] ?? null) === (b?.[k] ?? null));
}
function stripFixedFlag(p) {
  const { __fixed, ...rest } = p || {};
  return rest;
}
